/**
 * AccessCodePage — site-level PIN before login or any admin route.
 */
import { useState, type FormEvent } from 'react';
import { isAdminAccessCodeConfigured, isValidAdminAccessCode } from '../lib/accessCode';

interface AccessCodePageProps {
  onUnlock: () => void;
}

export function AccessCodePage({ onUnlock }: AccessCodePageProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isAdminAccessCodeConfigured()) {
      setError('Admin access code is not configured for this environment (VITE_ADMIN_ACCESS_CODE).');
      return;
    }

    if (!isValidAdminAccessCode(code)) {
      setError('That access code is not valid.');
      setCode('');
      return;
    }

    onUnlock();
  };

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{ backgroundColor: 'var(--bg)' }}
    >
      <div className="w-full max-w-sm">
        <div
          className="rounded-lg overflow-hidden"
          style={{
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}
        >
          <div className="px-6 py-5" style={{ backgroundColor: '#1F1E26' }}>
            <img
              src="/logo.svg"
              alt="PazNetworks, Inc."
              style={{ height: '44px', width: 'auto' }}
            />
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginTop: '6px' }}>
              Admin Workspace
            </p>
          </div>

          <div className="px-6 py-5">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <h1 className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                  Enter access code
                </h1>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  This workspace requires an access code before sign-in.
                </p>
              </div>

              <div>
                <label
                  htmlFor="access-code"
                  className="block text-xs font-medium mb-1"
                  style={{ color: 'var(--text-muted)' }}
                >
                  Access code
                </label>
                <input
                  id="access-code"
                  type="password"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="Enter access code"
                  className="w-full h-9 px-3 text-sm rounded-sm outline-none transition-colors"
                  style={{
                    backgroundColor: 'var(--surface)',
                    border: `1px solid ${error ? 'var(--error)' : 'var(--border)'}`,
                    color: 'var(--text)',
                  }}
                />
              </div>

              {error && (
                <div
                  className="px-3 py-2 rounded-sm"
                  style={{
                    backgroundColor: 'var(--error-soft)',
                    border: '1px solid var(--error)',
                  }}
                >
                  <p className="text-xs" style={{ color: 'var(--error)' }}>{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={!code.trim()}
                className="w-full h-9 rounded-sm text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-primary text-text-inverse hover:bg-primary-hover"
              >
                Continue
              </button>
            </form>
          </div>
        </div>

        <p className="text-center text-[10px] mt-4" style={{ color: 'var(--text-faint)' }}>
          &copy; {new Date().getFullYear()} PazNetworks, Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
}
